import React from 'react';
import Styled from 'styled-components';
import {useIntl} from 'react-intl';
import {
	Accordion,
	FlexColumn,
	FlexRow,
	FlexItem,
	Label,
	SegmentedControl,
	TextField,
	DropdownList,
	ButtonDropdownField,
	Text,
	Toggle,
	IconTooltip,
} from '@forecast-it/design-system';
import {BUDGET_TYPE, PERIOD_BUDGET_TYPE, PERIODICITY} from '../../../constants';
import type {ProjectProposal, RetainerScope} from './types';
import {addMonthsClamped} from './opportunityStage';
import {TEXT_AUX} from './colors';

// The inline "Every [N]" / "Create [N]" inputs are a fixed 62px wide and 40px tall, not stretched
// to fill their flex slot, so the Retainer rows stay aligned with each other. The design's inline
// number box is 40px, a height the design-system TextField sizes (32px and 44px) do not offer.
const InlineNumberField = Styled.div`
	width: 62px;
	flex: 0 0 62px;

	input {
		height: 40px;
	}
`;

// A disabled budget-type segment reads as greyed text on the
// track — no stroke, no fill. The design system's own SegmentedControl :disabled rule paints both,
// for every SegmentedControl in the app; overridden here for this modal only, since the app-wide
// fix belongs in the design system itself.
const BudgetTypeControl = Styled.div`
	button:disabled {
		border-color: transparent;
		background-color: transparent;
	}
`;

// TNSR-635 review (Animesh's manual QA against Figma, nodes 336:10082/11755/12493/13770): the
// "Dates"/"Total Value" summary at the bottom of the Retainer fields is read-only, computed from
// the project's start date plus Invoice Frequency × Pre-Create Periods — mirrors
// new_project_modal_detailed_settings.js's own identical `lastPeriodEndDate` computation (its
// moment-based add/subtract), reimplemented on plain Date since nothing else in this modal pulls
// in moment. addMonthsClamped itself lives in opportunityStage.ts, shared with the equivalent
// submission-side loop in AiProjectPlanningModal.tsx (TNSR-635 review, Lakshmi) so the preview
// and what submission actually creates can't diverge by one copy getting a fix the other doesn't.

const addRetainerPeriod = (start: Date, periodicity: string, totalUnits: number): Date => {
	let end: Date;
	if (periodicity === PERIODICITY.DAILY) {
		end = new Date(start.getTime());
		end.setDate(end.getDate() + totalUnits);
	} else if (periodicity === PERIODICITY.WEEKLY) {
		end = new Date(start.getTime());
		end.setDate(end.getDate() + totalUnits * 7);
	} else {
		end = addMonthsClamped(start, totalUnits);
	}
	end.setDate(end.getDate() - 1);
	return end;
};

const formatDMY = (d: Date): string =>
	`${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;

// TNSR-635 review (Lakshmi): `Number(value)` plus `>= 0` alone accepts floats and arbitrarily
// large values on both fields below. preCreateAmount drives a sequential, uncancellable
// per-period mutation loop after submit (AiProjectPlanningModal.tsx) — "500" (a plausible
// fat-finger for "5", or a paste) fires 500 round-trips, and "2.7" ran the loop 3 times because
// `i < 2.7`. periodLength gets a floor of 1 as well as a ceiling: `0` makes the loop's own
// advancePeriodStart return the same date forever, so every pre-created period lands on the
// project start date instead of advancing. The 999/60 ceilings are generous for any real retainer
// (the smaller one still allows 5 years of monthly pre-created periods in one submission) while
// ruling out the fat-finger/paste cases above.
const isValidPeriodLength = (parsed: number): boolean => Number.isInteger(parsed) && parsed >= 1 && parsed <= 999;
const isValidPreCreateAmount = (parsed: number): boolean => Number.isInteger(parsed) && parsed >= 0 && parsed <= 60;

export interface FinancialSetupSectionProps {
	proposal: ProjectProposal;
	rateCards: {id: string; name: string; currency: string}[];
	currency: string;
	onProposalChange: (proposal: ProjectProposal) => void;
	onFieldUserEdited: (field: string) => void;
	// Whether the viewer has INVOICE_CREATE permission — computed once by the parent (it doesn't
	// change during the modal's lifetime) and threaded down rather than re-checked here, so there's
	// one source of truth for both whether the toggle renders and what it defaults to.
	canCreateInvoices: boolean;
	createInvoicesIfAvailable: boolean;
	onCreateInvoicesIfAvailableChange: (value: boolean) => void;
	// TNSR-635 review (Animesh's manual QA against Figma, design note node 334:6464): "Create
	// Invoices if Available" is only meaningful once a SoW is attached — the no-file flow's own
	// CreateProjectMutation call never reads it. true once uploadRegionState !== 'no-file', but
	// unlike Retainer/Non-Billable and Duplicate Project, NOT re-true again on cancellation —
	// see AiProjectPlanningModal.tsx's own createInvoicesAllowed comment for why rejection and
	// cancellation aren't equivalent for this one.
	createInvoicesAllowed: boolean;
	// TNSR-635 review (Animesh's manual QA against Figma): Retainer/Non-Billable are selectable in
	// the normal (no-file) flow, but go back to disabled once a SoW document is attached — a
	// distinct rule from the plain-create-vs-Opportunities-only one this section's own Retainer/
	// Non-Billable fix already covers.
	retainerAndNonBillableDisabled: boolean;
	// Retainer's own per-period settings (TNSR-635 review — Figma's Retainer frames), submitted as
	// CreateProjectInput's defaultPeriod* fields.
	retainerScope: RetainerScope;
	onRetainerScopeChange: (scope: RetainerScope) => void;
	// Rate Card and Use Baseline: real, interactive fields, submitted as rateCardId/useBaseline —
	// the old NEW_PROJECT_V2 modal this ticket replaced always sent both (for create; Use Baseline
	// specifically only outside RETAINER, matching that modal's own guard), so caught as a real
	// regression rather than left as scope this rebuild deliberately deferred, unlike Salesperson/
	// Win Probability/Project ID (never sent even by the old modal — see OverviewSection.tsx).
	rateCardSelected: {id: string; name: string; currency: string} | null;
	onRateCardSelectedChange: (rateCard: {id: string; name: string; currency: string} | null) => void;
	useBaseline: boolean;
	onUseBaselineChange: (value: boolean) => void;
}

const FinancialSetupSection = ({
	proposal,
	rateCards,
	currency,
	onProposalChange,
	onFieldUserEdited,
	canCreateInvoices,
	createInvoicesIfAvailable,
	onCreateInvoicesIfAvailableChange,
	createInvoicesAllowed,
	retainerAndNonBillableDisabled,
	retainerScope,
	onRetainerScopeChange,
	rateCardSelected,
	onRateCardSelectedChange,
	useBaseline,
	onUseBaselineChange,
}: FinancialSetupSectionProps): JSX.Element => {
	const {formatMessage} = useIntl();

	// Rate Card and Use Baseline are lifted to the caller (see FinancialSetupSectionProps' own
	// comment) — these two setters stay local aliases so the JSX/effect below reads the same as
	// before the lift.
	const setRateCardSelected = onRateCardSelectedChange;
	// ON by default for every budget type, matching CreateOpportunityModal. A ref tracks whether the
	// viewer has manually touched it, so the default doesn't clobber an explicit "off" if they flip
	// between budget types afterward.
	const setUseBaseline = onUseBaselineChange;
	const useBaselineTouchedRef = React.useRef(false);
	React.useEffect(() => {
		if (!useBaselineTouchedRef.current) {
			setUseBaseline(true);
		}
	}, [proposal.budget_type]);
	const handleUseBaselineChange = (checked: boolean) => {
		useBaselineTouchedRef.current = true;
		setUseBaseline(checked);
	};

	// A project's currency is its rate card's, so the Price
	// label and the Retainer price/total follow the selected rate card, falling back to the company
	// currency only when none is picked.
	const effectiveCurrency = rateCardSelected?.currency ?? currency;

	// Currency symbol for the Price label (e.g. USD -> "$"). Derivation copied verbatim from
	// CreateOpportunityModal.tsx's own identical one for its Price field.
	const currencySymbol = React.useMemo(() => {
		try {
			return (
				new Intl.NumberFormat(undefined, {style: 'currency', currency: effectiveCurrency})
					.formatToParts(0)
					.find(part => part.type === 'currency')?.value ?? effectiveCurrency
			);
		} catch {
			return effectiveCurrency;
		}
	}, [effectiveCurrency]);

	const formatCurrency = (amount: number): string => {
		try {
			return new Intl.NumberFormat(undefined, {style: 'currency', currency: effectiveCurrency}).format(amount);
		} catch {
			return `${currencySymbol}${amount}`;
		}
	};

	// Only meaningful once there's enough to compute from: a pre-create amount, a period length, and
	// a per-period amount for the picked budget type — new_project_modal_detailed_settings.js's own
	// `lastPeriodEndDate` gate, minus its start date.
	// Total Value is amount × periods and never needs a start date; only Dates does, and it says
	// so when one is missing (below).
	const retainerAmountsReady =
		proposal.budget_type === BUDGET_TYPE.RETAINER &&
		!!retainerScope.preCreateAmount &&
		!!retainerScope.periodLength &&
		(retainerScope.periodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE
			? !!retainerScope.periodPriceAmount
			: !!retainerScope.periodHoursAmount);

	const retainerTotalValue = React.useMemo(() => {
		if (!retainerAmountsReady) return null;
		return retainerScope.periodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE
			? formatCurrency((retainerScope.periodPriceAmount ?? 0) * retainerScope.preCreateAmount)
			: `${(retainerScope.periodHoursAmount ?? 0) * retainerScope.preCreateAmount} ${formatMessage({
					id: 'common.hours',
			  })}`;
	}, [retainerAmountsReady, retainerScope, effectiveCurrency]);

	const retainerDates = React.useMemo(() => {
		if (!retainerAmountsReady || !proposal.start_date) return null;
		const start = new Date(`${proposal.start_date}T00:00:00`);
		const end = addRetainerPeriod(
			start,
			retainerScope.periodicity,
			retainerScope.periodLength * retainerScope.preCreateAmount
		);
		return `${formatDMY(start)} → ${formatDMY(end)}`;
	}, [retainerAmountsReady, proposal.start_date, retainerScope]);
	// The same hint the old NEW_PROJECT_V2 retainer section gave when it had no start date to count from.
	const showStartDateHint = retainerAmountsReady && !retainerDates;

	return (
		<Accordion.Item
			value="financial"
			label={formatMessage({id: 'opportunity.section.financial'})}
			content=""
			data-cy="section-financial"
		>
			<FlexColumn gap="l">
				<FlexRow>
					<FlexColumn gap="xs">
						{/* Icon-help next to the label, matching this ticket's own Figma frames (node
							I240:1540;7885:100728, confirmed directly) — CreateOpportunityModal.tsx has no
							equivalent tooltip on this field, so this reuses the existing, accurate
							project_portfolio_report budget-type description rather than inventing new copy. */}
						<FlexRow gap="xs" alignItems="center">
							<Label>{formatMessage({id: 'opportunity.field.budget_type'})}</Label>
							<IconTooltip>
								<IconTooltip.Item
									title={formatMessage({
										id: 'project_portfolio_report.project_overview.budget_type.tooltip.description',
									})}
								/>
							</IconTooltip>
						</FlexRow>
						{/* All four budget types are selectable in the normal (no-file) flow — TNSR-635 review
							(Lakshmi, manual testing): Retainer/Non-Billable must not be blocked "in the normal
							flow". This modal is the general Create Project entry point (NEW_PROJECT_V2's
							replacement), not the Opportunities-only one, so CreateOpportunityModal.tsx's own
							disabling of those two segments (:741-777, correct there — an opportunity being
							pursued isn't a retainer/non-billable engagement) does not apply there; the old
							NEW_PROJECT_V2 modal this replaces allows all four. Once a SoW document is attached,
							though, Figma disables both again (retainerAndNonBillableDisabled) — a separate rule
							from the Opportunities one, caught in a later round of manual QA. Text verified
							directly against this ticket's Figma frames (fileKey 5iN9P3uZiqLtDd3itKnNxh, node
							1:3539): "Budget Type" / "Fixed Price" / "Time & Materials" / "Retainer" render
							exactly as these keys resolve, and "Non-Billable" (hyphenated) matches this
							codebase's universal convention (~60 other en.json keys) even though that one static
							mockup renders it without a hyphen. */}
						<BudgetTypeControl>
							<SegmentedControl
								value={proposal.budget_type}
								onValueChange={(value: string) => {
									// Radix's single ToggleGroup deselects on re-click (emits ''); ignore empty so a
									// value is always held (mirrors CreateOpportunityModal.tsx's onBudgetTypeChange).
									if (value) {
										onFieldUserEdited('budget_type');
										onProposalChange({...proposal, budget_type: value});
									}
								}}
								data-cy="ai-budget-type"
							>
								<SegmentedControl.Item
									value={BUDGET_TYPE.FIXED_PRICE}
									label={formatMessage({id: 'common.fixed_price'})}
									data-cy="ai-budget-FIXED_PRICE"
								>
									{formatMessage({id: 'common.fixed_price'})}
								</SegmentedControl.Item>
								<SegmentedControl.Item
									value={BUDGET_TYPE.TIME_AND_MATERIALS}
									label={formatMessage({id: 'common.time_material'})}
									data-cy="ai-budget-TIME_AND_MATERIALS"
								>
									{formatMessage({id: 'common.time_material'})}
								</SegmentedControl.Item>
								<SegmentedControl.Item
									value={BUDGET_TYPE.RETAINER}
									label={formatMessage({id: 'common.retainer'})}
									disabled={retainerAndNonBillableDisabled}
									data-cy="ai-budget-RETAINER"
								>
									{formatMessage({id: 'common.retainer'})}
								</SegmentedControl.Item>
								<SegmentedControl.Item
									value={BUDGET_TYPE.NON_BILLABLE}
									label={formatMessage({id: 'common.non-billable'})}
									disabled={retainerAndNonBillableDisabled}
									data-cy="ai-budget-NON_BILLABLE"
								>
									{formatMessage({id: 'common.non-billable'})}
								</SegmentedControl.Item>
							</SegmentedControl>
						</BudgetTypeControl>
					</FlexColumn>
				</FlexRow>
				{/* Top-aligned, matching CreateOpportunityModal.tsx's own Financial row (:785). */}
				<FlexRow gap="l" alignItems="flex-start">
					{proposal.budget_type === BUDGET_TYPE.FIXED_PRICE && (
						<FlexItem flex="1 1 0">
							{/* opportunity.field.price interpolates a currency symbol ("Price ($)"), matching
								CreateOpportunityModal.tsx's own identical Price field and this ticket's Figma
								frames, which show "Price ($)" (not the currency-less "Price" common.price
								would render). Gated on FIXED_PRICE — matching the pre-existing SowReviewFormPanel's
								own rule (a Time & Materials/Retainer project has no single budget amount to set
								here), a rule this rebuild had silently dropped until a self-review caught it. */}
							<TextField
								data-cy="ai-budget-amount"
								label={formatMessage({id: 'opportunity.field.price'}, {currency: currencySymbol})}
								value={proposal.budget != null ? String(proposal.budget) : ''}
								onValueChange={(value: string) => {
									const cleaned = value.replace(/,/g, '');
									const parsed = Number(cleaned);
									if (cleaned === '' || (!isNaN(parsed) && parsed >= 0)) {
										onFieldUserEdited('budget');
										onProposalChange({...proposal, budget: cleaned === '' ? null : parsed});
									}
								}}
							/>
						</FlexItem>
					)}
					{proposal.budget_type !== BUDGET_TYPE.NON_BILLABLE && (
						<FlexItem flex="1 1 0">
							<FlexColumn gap="xs">
								<Label>{formatMessage({id: 'common.rate_card'})}</Label>
								<DropdownList>
									<DropdownList.Trigger
										data-cy="ai-rate-card"
										aria-label={formatMessage({id: 'common.rate_card'})}
									>
										<ButtonDropdownField>
											{/* Blank when unset, like Salesperson/Client/Contact — Figma's empty state
											shows no "None" text here either. */}
											<Text>{rateCardSelected?.name}</Text>
										</ButtonDropdownField>
									</DropdownList.Trigger>
									<DropdownList.Content align="start" useTriggerWidth>
										{rateCards.map(rateCard => (
											<DropdownList.Item
												key={rateCard.id}
												data-cy={`ai-rate-card-${rateCard.id}`}
												onClick={() => setRateCardSelected(rateCard)}
											>
												<Text>{rateCard.name}</Text>
											</DropdownList.Item>
										))}
									</DropdownList.Content>
								</DropdownList>
							</FlexColumn>
						</FlexItem>
					)}
				</FlexRow>
				{/* Retainer's own per-period settings (TNSR-635 review — Figma nodes 336:10082/11755/
					12493/13770, "Create New Opportunity/Retainer[/Fixed Hours|/FP|/T&M]"). Field-for-field:
					Periods (billing periodicity) + Invoice Frequency ("Every N <unit>"), "Budget Each
					Period by" (Fixed Hours/Fixed Price/Time & Materials) whose paired amount field's own
					label changes with that choice, "Pre-Create Periods" ("Create N Periods now"), and a
					read-only Dates + Total Value summary once there's enough to compute one from. Mirrors
					new_project_modal_detailed_settings.js's own ProjectBudgetTypeControls retainer section
					(the old NEW_PROJECT_V2 modal this ticket replaces) — the one other place in this
					codebase that already drives these exact CreateProjectInput defaultPeriod* fields. */}
				{proposal.budget_type === BUDGET_TYPE.RETAINER && (
					<>
						<FlexRow gap="l" alignItems="flex-start">
							<FlexItem flex="1 1 0">
								<FlexColumn gap="xs">
									<Label>{formatMessage({id: 'ai_project_planning.retainer_periods'})}</Label>
									<DropdownList>
										<DropdownList.Trigger
											data-cy="ai-retainer-periodicity"
											aria-label={formatMessage({id: 'ai_project_planning.retainer_periods'})}
										>
											<ButtonDropdownField>
												<Text>
													{retainerScope.periodicity === PERIODICITY.DAILY
														? formatMessage({id: 'common.days'})
														: retainerScope.periodicity === PERIODICITY.WEEKLY
														? formatMessage({id: 'common.weeks'})
														: formatMessage({id: 'common.months'})}
												</Text>
											</ButtonDropdownField>
										</DropdownList.Trigger>
										<DropdownList.Content align="start" useTriggerWidth>
											{[PERIODICITY.MONTHLY, PERIODICITY.WEEKLY, PERIODICITY.DAILY].map(periodicity => (
												<DropdownList.Item
													key={periodicity}
													data-cy={`ai-retainer-periodicity-${periodicity}`}
													onClick={() => onRetainerScopeChange({...retainerScope, periodicity})}
												>
													<Text>
														{periodicity === PERIODICITY.DAILY
															? formatMessage({id: 'common.days'})
															: periodicity === PERIODICITY.WEEKLY
															? formatMessage({id: 'common.weeks'})
															: formatMessage({id: 'common.months'})}
													</Text>
												</DropdownList.Item>
											))}
										</DropdownList.Content>
									</DropdownList>
								</FlexColumn>
							</FlexItem>
							<FlexItem flex="1 1 0">
								<FlexColumn gap="xs">
									<Label>{formatMessage({id: 'ai_project_planning.retainer_invoice_frequency'})}</Label>
									<FlexRow gap="l" alignItems="center">
										<Text>{formatMessage({id: 'ai_project_planning.retainer_every'})}</Text>
										<InlineNumberField>
											<TextField
												data-cy="ai-retainer-period-length"
												value={String(retainerScope.periodLength)}
												onValueChange={(value: string) => {
													// TNSR-635 review (Lakshmi): unlike preCreateAmount below (whose floor is 0,
													// so empty-coerced-to-0 is a valid value), periodLength's floor is 1 - writing
													// 0 here was the exact value the floor was raised from 0 to 1 to exclude.
													// Treating an empty field the same as any other invalid entry (ignore it,
													// hold the last valid value) rather than special-casing it to 0 keeps this
													// path as bounded as every other rejected input.
													const parsed = Number(value);
													if (isValidPeriodLength(parsed)) {
														onRetainerScopeChange({...retainerScope, periodLength: parsed});
													}
												}}
											/>
										</InlineNumberField>
										<Text>
											{retainerScope.periodicity === PERIODICITY.DAILY
												? formatMessage({id: 'common.days'})
												: retainerScope.periodicity === PERIODICITY.WEEKLY
												? formatMessage({id: 'common.weeks'})
												: formatMessage({id: 'common.months'})}
										</Text>
									</FlexRow>
								</FlexColumn>
							</FlexItem>
						</FlexRow>
						<FlexRow gap="l" alignItems="flex-start">
							<FlexItem flex="1 1 0">
								<FlexColumn gap="xs">
									<Label>{formatMessage({id: 'ai_project_planning.retainer_budget_each_period_by'})}</Label>
									<DropdownList>
										<DropdownList.Trigger
											data-cy="ai-retainer-period-budget-type"
											aria-label={formatMessage({
												id: 'ai_project_planning.retainer_budget_each_period_by',
											})}
										>
											<ButtonDropdownField>
												<Text>
													{retainerScope.periodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE
														? formatMessage({id: 'common.fixed_price'})
														: retainerScope.periodBudgetType ===
														  PERIOD_BUDGET_TYPE.TIME_AND_MATERIALS
														? formatMessage({id: 'ai_project_planning.budget_time_materials'})
														: formatMessage({id: 'ai_project_planning.retainer_fixed_hours'})}
												</Text>
											</ButtonDropdownField>
										</DropdownList.Trigger>
										<DropdownList.Content align="start" useTriggerWidth>
											{[
												PERIOD_BUDGET_TYPE.FIXED_HOURS,
												PERIOD_BUDGET_TYPE.FIXED_PRICE,
												PERIOD_BUDGET_TYPE.TIME_AND_MATERIALS,
											].map(periodBudgetType => (
												<DropdownList.Item
													key={periodBudgetType}
													data-cy={`ai-retainer-period-budget-type-${periodBudgetType}`}
													onClick={() => onRetainerScopeChange({...retainerScope, periodBudgetType})}
												>
													<Text>
														{periodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE
															? formatMessage({id: 'common.fixed_price'})
															: periodBudgetType === PERIOD_BUDGET_TYPE.TIME_AND_MATERIALS
															? formatMessage({id: 'ai_project_planning.budget_time_materials'})
															: formatMessage({id: 'ai_project_planning.retainer_fixed_hours'})}
													</Text>
												</DropdownList.Item>
											))}
										</DropdownList.Content>
									</DropdownList>
								</FlexColumn>
							</FlexItem>
							<FlexItem flex="1 1 0">
								{retainerScope.periodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE ? (
									<TextField
										data-cy="ai-retainer-period-price"
										// Follows the rate card's currency, like Price above.
										label={formatMessage(
											{id: 'ai_project_planning.retainer_price_per_period'},
											{currency: currencySymbol}
										)}
										value={
											retainerScope.periodPriceAmount != null
												? String(retainerScope.periodPriceAmount)
												: ''
										}
										onValueChange={(value: string) => {
											const cleaned = value.replace(/,/g, '');
											const parsed = Number(cleaned);
											if (cleaned === '' || (!isNaN(parsed) && parsed >= 0)) {
												onRetainerScopeChange({
													...retainerScope,
													periodPriceAmount: cleaned === '' ? null : parsed,
												});
											}
										}}
									/>
								) : (
									<TextField
										data-cy="ai-retainer-period-hours"
										label={formatMessage({
											id:
												retainerScope.periodBudgetType === PERIOD_BUDGET_TYPE.TIME_AND_MATERIALS
													? 'ai_project_planning.retainer_target_hours_per_period'
													: 'ai_project_planning.retainer_hours_per_period',
										})}
										value={
											retainerScope.periodHoursAmount != null
												? String(retainerScope.periodHoursAmount)
												: ''
										}
										onValueChange={(value: string) => {
											const cleaned = value.replace(/,/g, '');
											const parsed = Number(cleaned);
											if (cleaned === '' || (!isNaN(parsed) && parsed >= 0)) {
												onRetainerScopeChange({
													...retainerScope,
													periodHoursAmount: cleaned === '' ? null : parsed,
												});
											}
										}}
									/>
								)}
							</FlexItem>
						</FlexRow>
						<FlexRow gap="l" alignItems="flex-start">
							<FlexItem flex="0 0 238px">
								<FlexColumn gap="xs">
									<Label>{formatMessage({id: 'ai_project_planning.retainer_pre_create_periods'})}</Label>
									<FlexRow gap="l" alignItems="center">
										<Text>{formatMessage({id: 'ai_project_planning.retainer_create'})}</Text>
										<InlineNumberField>
											<TextField
												data-cy="ai-retainer-pre-create-amount"
												value={String(retainerScope.preCreateAmount)}
												onValueChange={(value: string) => {
													const parsed = Number(value);
													if (value === '' || isValidPreCreateAmount(parsed)) {
														onRetainerScopeChange({
															...retainerScope,
															preCreateAmount: value === '' ? 0 : parsed,
														});
													}
												}}
											/>
										</InlineNumberField>
										<Text>{formatMessage({id: 'ai_project_planning.retainer_periods_now'})}</Text>
									</FlexRow>
								</FlexColumn>
							</FlexItem>
							{/* TNSR-635 review (Animesh's manual QA): renders unconditionally, matching Figma's
								own always-visible Dates/Total Value block — Salesperson/Client/Contact/Rate
								Card all follow this same "blank when unset, never hidden" rule elsewhere in
								this form (see the Rate Card field's own comment above). retainerTotals is
								only null when there isn't yet enough to compute from (no start date, or the
								relevant per-period amount not filled in) — that renders as blank text, not a
								missing row.
								height 44px, vertically centred: Figma's own "date-fields" nodes are hardcoded
								h-[44px] + items-center — matching the TextField's own height so this text's
								baseline lines up with "Create [N] Periods now" alongside it (that row's height
								is driven by its own TextField), not the shorter, top-aligned line-height plain
								Text would otherwise get on its own. FlexRow itself has no height/style prop
								(Flex.styled.js's RowContainer doesn't expose one), hence the plain div. */}
							<FlexItem flex="1 1 0">
								{/* Total Value is right-aligned to the section
									edge. Both columns are flex="0 0 auto": RowContainer otherwise stretches each
									un-flexed child to flex:1, which left space-between no free space to distribute
									and parked Total Value mid-row. */}
								<FlexRow gap="l" alignItems="flex-start" justifyContent="space-between">
									<FlexItem flex="0 0 auto">
										<FlexColumn gap="xs">
											<Label>{formatMessage({id: 'common.dates'})}</Label>
											<div style={{height: '44px', display: 'flex', alignItems: 'center'}}>
												<Text data-cy="ai-retainer-dates">
													{showStartDateHint ? (
														<span style={{color: TEXT_AUX}}>
															{formatMessage({id: 'common.select_start_date'})}
														</span>
													) : (
														retainerDates ?? ''
													)}
												</Text>
											</div>
										</FlexColumn>
									</FlexItem>
									<FlexItem flex="0 0 auto">
										<FlexColumn gap="xs" alignItems="flex-end">
											<Label>{formatMessage({id: 'ai_project_planning.retainer_total_value'})}</Label>
											<div style={{height: '44px', display: 'flex', alignItems: 'center'}}>
												<Text data-cy="ai-retainer-total-value">{retainerTotalValue ?? ''}</Text>
											</div>
										</FlexColumn>
									</FlexItem>
								</FlexRow>
							</FlexItem>
						</FlexRow>
					</>
				)}
				{/* Use Baseline: switch + label in a centred FlexRow. TNSR-635 review (Animesh's manual QA
					against Figma directly): the toggle sits BEFORE its label, not after — an earlier pass
					copied CreateOpportunityModal.tsx's own Text-before-Toggle order (:863-868) verbatim,
					which is wrong for this modal's own Figma frames.
					Note: the design-system Toggle hardcodes aria-label="toggle" on its underlying switch
					and forwards no other aria attributes, so this Text is not programmatically associated
					with the switch by accessible name — a pre-existing design-system limitation already hit
					and documented elsewhere in this codebase (ProjectClientPortal.js/.test.tsx), not
					something introduced here. FinancialSetupSection.test.tsx queries the switch by its
					data-cy instead of by accessible name for this reason. */}
				<FlexRow gap="l" alignItems="center">
					{/* flex="0 0 auto": RowContainer defaults an un-flexed child to flex:1, which would let
						each toggle group stretch to fill its share of the row and space the two groups apart
						instead of sitting snug together with just the 16px `gap` between them (Figma node
						334:5010, confirmed directly). */}
					<FlexRow gap="s" alignItems="center" flex="0 0 auto">
						<Toggle checked={useBaseline} onChange={handleUseBaselineChange} cy="ai-use-baseline" />
						<Text>{formatMessage({id: 'opportunity.field.use_baseline'})}</Text>
					</FlexRow>
					{/* Per product's TNSR-635 ruling: this toggle must not render at all for a viewer who
						lacks INVOICE_CREATE permission — not shown-disabled, fully absent (Dylan Connerton,
						ticket comment: "It should be invisible"). Matches Figma's Financial Setup row,
						which places it directly beside Use Baseline.
						TNSR-635 review (Animesh's manual QA): a second, independent gate — the design note
						sitting inside this same Figma file (node 334:6464) says outright "'Create invoices
						if available' only allowed once SoW has been uploaded." The normal (no-file) flow's
						own CreateProjectMutation call never reads createInvoicesIfAvailable at all (only
						resumeAgent's SoW-path commit does — see AiProjectPlanningModal.tsx), so offering it
						in that flow was worse than just a copy mismatch: the control did nothing. */}
					{canCreateInvoices && createInvoicesAllowed && (
						<FlexRow gap="s" alignItems="center" flex="0 0 auto">
							<Toggle
								checked={createInvoicesIfAvailable}
								onChange={onCreateInvoicesIfAvailableChange}
								cy="ai-create-invoices-if-available"
							/>
							<Text>{formatMessage({id: 'ai_project_planning.create_invoices_if_available'})}</Text>
						</FlexRow>
					)}
				</FlexRow>
			</FlexColumn>
		</Accordion.Item>
	);
};

export default FinancialSetupSection;
